import { RhInputFormik } from "@rhythm-ui/react";
import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { RootState } from "@/redux/types";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";

import { OpportunityContext } from "../constants";
import { setIsCulturalDiscussionScheduledDialogOpen } from "../stageAndStatusPopoverForCandidateOpportunitiesSlice";
import { FormSubmissionValues } from "../types";

type TCulturalDiscussionDialogProps = {
  handleSubmit: (values: FormSubmissionValues, status?: string) => void;
};

const CulturalDiscussionDialog = ({
  handleSubmit,
}: TCulturalDiscussionDialogProps) => {
  const opportunity = useContext(OpportunityContext);
  const dispatch = useDispatch();

  const isOpen = useSelector(
    (state: RootState) =>
      state.stageAndStatusPopoverForCandidateOpportunitiesSlice
        .isCulturalDiscussionScheduledDialogOpen,
  );

  if (
    isOpen.id !== opportunity?.job_id ||
    isOpen.candidate_id !== opportunity.candidate_id
  )
    return null;

  const handleClose = () => {
    dispatch(
      setIsCulturalDiscussionScheduledDialogOpen({
        id: opportunity?.job_id,
        open: false,
        candidate_id: opportunity.candidate_id,
        status: isOpen.status,
      }),
    );
  };

  const onSubmit = (values: FormSubmissionValues) => {
    handleSubmit(values, isOpen.status);
    handleClose();
  };

  const isReschedule = isOpen.status === "rescheduled";

  return (
    <Dialog isOpen={isOpen.open} className="w-[600px]">
      <DialogHeader>
        <DialogTitle>
          {isReschedule
            ? "Cultural Discussion Rescheduled"
            : "Cultural Discussion Scheduled"}
        </DialogTitle>

        <DialogDescription>
          {isReschedule
            ? "Provide new date and reason for rescheduling"
            : "Mention date and time for cultural discussion round"}
        </DialogDescription>
      </DialogHeader>

      <Formik
        initialValues={{ detailed_reason: "" }}
        onSubmit={onSubmit}
        validationSchema={Yup.object().shape({
          detailed_reason: Yup.string().required("Please provide a comment"),
        })}
      >
        <Form>
          <DialogBody>
            <RhInputFormik
              name="detailed_reason"
              type="textarea"
              className="w-full"
              rows={4}
              autoFocus
              placeholder={
                isReschedule
                  ? "Enter rescheduling notes"
                  : "Enter scheduling notes"
              }
            />
          </DialogBody>

          <DialogFooter>
            <Button variant="tertiary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">Submit</Button>
          </DialogFooter>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default CulturalDiscussionDialog;
